import {useCallback, useEffect, useMemo, useState} from 'react'
import MUITable from '../../modules/common/MUITable'
import TableLoading from '../../../_metronic/layout/components/Common/TableSkeletonLoading'
import {KTSVG} from '../../../_metronic/helpers'
import {getChannelCategoriesRequest} from '../../../api/endpoints/channelCategory'
import {getAllServices} from '../../../api/endpoints/service'
import {SHOW_ERROR} from '../../../util'
import AssignChannelsModal from './components/AssignChannelsModal'
import {getCategoryChannels, toList} from './utils'


const ChannelCategories = () => {
  const [categories, setCategories] = useState<any[]>([])
  const [channels, setChannels] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<any>(null)

  const [currentPage, setCurrentPage] = useState(1)
  const [entriesPerPage, setEntriesPerPage] = useState(10)

  const fetchCategories = useCallback(async () => {
    try {
      const res: any = await getChannelCategoriesRequest()
      setCategories(toList(res?.data?.data))
    } catch (error: any) {
      SHOW_ERROR(true, error?.response?.data?.message ?? 'Failed to load categories')
    }
  }, [])

  const fetchChannels = useCallback(async () => {
    try {
      const res: any = await getAllServices({level: 1, parentId: null})
      // Hide general channels, same as the channels page (servicesReducer level 1)
      setChannels(toList(res?.data?.data).filter((ch: any) => !ch?.isGeneral))
    } catch (error: any) {
      SHOW_ERROR(true, error?.response?.data?.message ?? 'Failed to load channels')
    }
  }, [])

  useEffect(() => {
    Promise.all([fetchCategories(), fetchChannels()]).finally(() => setLoading(false))
  }, [fetchCategories, fetchChannels])

  const filteredCategories = useMemo(
    () =>
      categories.filter((c) => c?.name?.toLowerCase().includes(search.trim().toLowerCase())),
    [categories, search]
  )

  const columns = [
    {
      id: 'index',
      label: '#',
      align: 'left' as const,
      render: (_: any, index: number) => <span>{(currentPage - 1) * entriesPerPage + index + 1}</span>,
    },
    {
      id: 'name',
      label: 'Name',
      align: 'left' as const,
      render: (row: any) => (
        <div className='d-flex align-items-center'>
          {row?.icon && (
            <img src={row.icon} alt='icon' style={{width: 30, height: 30, marginRight: 10}} />
          )}
          <span>{row?.name || '—'}</span>
        </div>
      ),
    },
    {
      id: 'channels',
      label: 'Channels',
      align: 'left' as const,
      render: (row: any) => {
        const assigned = getCategoryChannels(row)
        if (!assigned.length) return <span className='text-muted'>No channels</span>
        return (
          <div className='d-flex flex-wrap gap-1' style={{maxWidth: 500}}>
            {assigned.map((ch: any) => (
              <span key={ch?.id} className='badge badge-light-primary'>
                {ch?.name}
              </span>
            ))}
          </div>
        )
      },
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'center' as const,
      render: (row: any) => (
        <button
          type='button'
          className='btn btn-sm btn-primary'
          onClick={() => setSelectedCategory(row)}
        >
          Assign Channels
        </button>
      ),
    },
  ]

  const tableData = useMemo(() => {
    const total = filteredCategories.length
    const start = (currentPage - 1) * entriesPerPage
    const rows = filteredCategories.slice(start, start + entriesPerPage).map((row, index) => {
      const newRow: any = {}
      columns.forEach((col) => {
        newRow[col.id] = col.render(row, index)
      })
      return newRow
    })
    return {
      table: {columns, rows},
      pagination: {
        total,
        lastPage: Math.max(1, Math.ceil(total / entriesPerPage)),
        currentPage,
        entriesPerPage,
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCategories, currentPage, entriesPerPage])

  // Keep the open modal in sync with freshly fetched category data.
  const activeCategory = selectedCategory
    ? categories.find((c) => c.id === selectedCategory.id) ?? selectedCategory
    : null

  return (
    <>
      <div className='row mb-5 mt-n5 pt-n5 align-items-center'>
        <div className='col-6'>
          <h2 className='py-3 mb-0'>Channel Categories</h2>
        </div>
      </div>
      <div className='card py-5 px-8'>
        <div className='d-flex justify-content-between align-items-center mb-2'>
          <div className='d-flex align-items-center position-relative'>
            <KTSVG
              path='/media/icons/duotune/general/gen021.svg'
              className='svg-icon-1 position-absolute ms-4'
            />
            <input
              type='text'
              className='form-control form-control-solid w-250px ps-14'
              placeholder='Search categories'
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setCurrentPage(1)
              }}
            />
          </div>
          <div
            style={{cursor: 'auto'}}
            className='btn btn-sm btn-icon btn-color-white border-0 bg-primary'
          >
            {filteredCategories.length}
          </div>
        </div>
        <div className='row mt-5'>
          {loading ? (
            <TableLoading />
          ) : (
            <div className='card-body'>
              <MUITable
                data={tableData}
                page={currentPage}
                perPage={entriesPerPage}
                setPage={setCurrentPage}
                setPerPage={(n: number) => {
                  setEntriesPerPage(n)
                  setCurrentPage(1)
                }}
              />
            </div>
          )}
        </div>
      </div>

      {activeCategory && (
        <AssignChannelsModal
          category={activeCategory}
          categories={categories}
          channels={channels}
          onClose={() => setSelectedCategory(null)}
          onAssigned={fetchCategories}
        />
      )}
    </>
  )
}

export default ChannelCategories
